import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Calendar, Ticket, Radio, Clock, Globe, Bell, BellOff, Loader2, Sparkles, Film,
  Users, Mail, Plus, Minus, X, Copy, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { subscribePush, pushSupported } from "../lib/push";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import GCRAdBanner from "../components/GCRAdBanner";

const VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const TZ_ABBR = (() => {
  try { return new Date().toLocaleTimeString("en-US", { timeZoneName: "short" }).split(" ").pop(); } catch { return ""; }
})();

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

function fmtDayLong(date) {
  return date.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
}

function isoToLocalDateKey(iso) {
  const d = new Date(iso);
  // YYYY-MM-DD in viewer's local tz
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function groupByLocalDate(shows) {
  // The backend returns days bucketed by UTC date; for viewers in non-UTC tz a show at 23:00 UTC
  // might actually be tomorrow in their local calendar. Re-bucket by local-date for correctness.
  const map = new Map();
  for (const s of shows) {
    const key = isoToLocalDateKey(s.starts_at);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  }
  // Sort each bucket by starts_at
  for (const arr of map.values()) arr.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  return map;
}

export default function Showtimes() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDate, setActiveDate] = useState(null);   // local-date key "YYYY-MM-DD"
  const [reminders, setReminders] = useState([]);       // schedule_ids the user has reminders on
  const [buying, setBuying] = useState(null);           // schedule_id currently being purchased
  const [groupFor, setGroupFor] = useState(null);       // show object for which the group-booking modal is open
  const [groupSession, setGroupSession] = useState(null); // post-checkout state (read from ?group_session_id=)

  // Load 14-day window
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/gcr/schedule/calendar", { params: { days: 14 } });
        if (!cancelled) {
          setData(data);
          const initial = sp.get("date") || (() => {
            // Default to today in viewer's local tz
            const t = new Date();
            return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
          })();
          setActiveDate(initial);
        }
      } catch (e) {
        toast.error("Could not load showtimes");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Reminders for the logged-in user
  const refreshReminders = async () => {
    try {
      const { data } = await api.get("/gcr/reminders");
      setReminders((data || []).map((r) => r.schedule_id));
    } catch (e) {
      console.warn("[showtimes] reminders fetch failed", e?.response?.status);
    }
  };
  useEffect(() => { refreshReminders(); }, []);

  // Pivot the backend's UTC-bucketed shows into local-date buckets
  const byLocalDate = useMemo(() => {
    if (!data?.calendar) return new Map();
    const all = data.calendar.flatMap((d) => d.shows);
    return groupByLocalDate(all);
  }, [data]);

  // Build the date-strip from the local-date buckets we actually have shows for,
  // up to 14 days from today
  const dateStrip = useMemo(() => {
    if (!data) return [];
    const out = [];
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    for (let i = 0; i < (data.days || 14); i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const shows = byLocalDate.get(key) || [];
      out.push({
        key,
        date: d,
        weekday: d.toLocaleDateString([], { weekday: "short" }).toUpperCase(),
        day: d.getDate(),
        month: d.toLocaleDateString([], { month: "short" }).toUpperCase(),
        is_today: i === 0,
        count: shows.length,
        premium_count: shows.filter((s) => s.is_premium_event).length,
      });
    }
    return out;
  }, [data, byLocalDate]);

  const setDate = (key) => {
    setActiveDate(key);
    sp.set("date", key);
    setSp(sp, { replace: true });
  };

  const showsForActive = useMemo(() => byLocalDate.get(activeDate) || [], [byLocalDate, activeDate]);
  const activeDateObj = useMemo(() => {
    if (!activeDate) return null;
    const [y, m, d] = activeDate.split("-").map(Number);
    return new Date(y, m - 1, d);
  }, [activeDate]);

  // Group active-day shows by time-of-day slot for theater feel: Morning / Afternoon / Evening / Late Night
  const grouped = useMemo(() => {
    const buckets = { Morning: [], Afternoon: [], Evening: [], "Late Night": [] };
    for (const s of showsForActive) {
      const h = new Date(s.starts_at).getHours();
      if (h < 12) buckets.Morning.push(s);
      else if (h < 17) buckets.Afternoon.push(s);
      else if (h < 22) buckets.Evening.push(s);
      else buckets["Late Night"].push(s);
    }
    return Object.entries(buckets).filter(([, arr]) => arr.length > 0);
  }, [showsForActive]);

  const toggleReminder = async (s) => {
    if (!pushSupported()) { toast.error("Notifications not supported on this browser"); return; }
    const hasIt = reminders.includes(s.schedule_id);
    try {
      if (hasIt) {
        await api.delete(`/gcr/reminders/${s.schedule_id}`);
        toast.success("Reminder removed");
      } else {
        await subscribePush().catch(() => {});
        await api.post("/gcr/reminders", { schedule_id: s.schedule_id });
        toast.success(`We'll ping you before ${s.title} starts`);
      }
      refreshReminders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update reminder");
    }
  };

  const buyTicket = async (s) => {
    setBuying(s.schedule_id);
    try {
      const { data } = await api.post("/gcr/tickets/checkout", {
        schedule_id: s.schedule_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBuying(null);
    }
  };

  // ---- Group booking post-checkout success poll ----
  useEffect(() => {
    const sid = sp.get("group_session_id");
    if (!sid) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        // Lookup the buyer's group via /my-tickets (filter by session_id locally)
        const { data: tickets } = await api.get("/gcr/tickets/mine");
        const mine = (tickets || []).find((t) => t.session_id === sid && t.is_group_host);
        if (mine?.group_id && mine.status === "paid") {
          const { data: group } = await api.get(`/gcr/tickets/group/${mine.group_id}`);
          if (!cancelled) {
            setGroupSession(group);
            sp.delete("group_session_id");
            setSp(sp, { replace: true });
            toast.success("Group booking confirmed!");
          }
          return;
        }
      } catch (e) {
        console.warn("[showtimes] group session poll failed", e?.response?.status);
      }
      if (attempts < 10 && !cancelled) setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-12">
        <button
          onClick={() => nav("/live")}
          data-testid="showtimes-back"
          className="text-sm text-gray-400 hover:text-white flex items-center gap-1.5 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to channels
        </button>

        <header className="mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="bg-gradient-to-br from-amber-500 to-[#E50914] p-2.5 rounded-lg">
              <Calendar className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-3xl md:text-4xl font-black tracking-tight">Showtimes</h1>
              <p className="text-gray-400 text-sm mt-1 flex items-center gap-2 flex-wrap">
                <Globe className="w-3.5 h-3.5" />
                Times shown in <span className="text-white font-semibold">{VIEWER_TZ}</span> {TZ_ABBR && <span className="text-gray-500">({TZ_ABBR})</span>}
              </p>
            </div>
          </div>
        </header>

        <GCRAdBanner position="top" />

        {/* Date strip — horizontal scroll, 14 days */}
        {loading ? (
          <div className="py-12 flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading schedule…
          </div>
        ) : (
          <>
            <div data-testid="date-strip" className="overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide mb-6">
              <div className="flex gap-2 min-w-max">
                {dateStrip.map((d) => {
                  const active = d.key === activeDate;
                  return (
                    <button
                      key={d.key}
                      data-testid={`date-tile-${d.key}`}
                      onClick={() => setDate(d.key)}
                      className={`relative shrink-0 w-[88px] py-3 px-2 rounded-lg border-2 text-center transition-all ${
                        active
                          ? "border-[#E50914] bg-[#E50914]/15 text-white"
                          : "border-white/10 hover:border-white/30 bg-[#0f0f0f] text-gray-300"
                      }`}
                    >
                      <div className={`text-[10px] uppercase tracking-widest font-bold ${active ? "text-[#E50914]" : "text-gray-500"}`}>
                        {d.is_today ? "Today" : d.weekday}
                      </div>
                      <div className="text-3xl font-black leading-none mt-1">{d.day}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{d.month}</div>
                      {d.count > 0 ? (
                        <div className="mt-1.5 text-[10px] font-semibold">
                          <span className="text-gray-300">{d.count}</span>
                          <span className="text-gray-500"> show{d.count === 1 ? "" : "s"}</span>
                          {d.premium_count > 0 && (
                            <span className="block text-amber-400 mt-0.5">{d.premium_count} 🎫</span>
                          )}
                        </div>
                      ) : (
                        <div className="mt-1.5 text-[10px] text-gray-600">No shows</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {activeDateObj && (
              <h2 className="text-xl md:text-2xl font-bold mb-1 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#E50914]" />
                {fmtDayLong(activeDateObj)}
              </h2>
            )}
            <p className="text-sm text-gray-500 mb-6">
              {showsForActive.length === 0 ? "No shows scheduled." : (
                <>
                  {showsForActive.length} show{showsForActive.length === 1 ? "" : "s"} ·{" "}
                  {showsForActive.filter((s) => s.is_premium_event).length} ticketed premium event
                  {showsForActive.filter((s) => s.is_premium_event).length === 1 ? "" : "s"}
                </>
              )}
            </p>

            {showsForActive.length === 0 && (
              <div data-testid="showtimes-empty" className="bg-[#0f0f0f] border border-white/5 rounded-lg p-12 text-center">
                <Film className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No scheduled shows on this date.</p>
              </div>
            )}

            {grouped.map(([bucketName, items]) => (
              <section key={bucketName} className="mb-8" data-testid={`time-bucket-${bucketName.toLowerCase().replace(" ", "-")}`}>
                <h3 className="text-sm uppercase tracking-widest font-bold text-gray-400 mb-3 flex items-center gap-2">
                  <span className="w-1 h-4 bg-[#E50914] rounded" /> {bucketName}
                  <span className="text-gray-500 font-normal">· {items.length} show{items.length === 1 ? "" : "s"}</span>
                </h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {items.map((s) => (
                    <ShowCard
                      key={s.schedule_id + s.starts_at}
                      show={s}
                      hasReminder={reminders.includes(s.schedule_id)}
                      onToggleReminder={() => toggleReminder(s)}
                      onBuy={() => buyTicket(s)}
                      onBuyGroup={() => setGroupFor(s)}
                      buying={buying === s.schedule_id}
                    />
                  ))}
                </div>
              </section>
            ))}

            <GCRAdBanner position="bottom" />
          </>
        )}
      </main>
      {groupFor && (
        <GroupBookingModal
          show={groupFor}
          onClose={() => setGroupFor(null)}
        />
      )}
      {groupSession && (
        <GroupSuccessModal
          group={groupSession}
          onClose={() => setGroupSession(null)}
        />
      )}
      <Footer />
    </div>
  );
}

function ShowCard({ show, hasReminder, onToggleReminder, onBuy, onBuyGroup, buying }) {
  const nav = useNavigate();
  const start = new Date(show.starts_at);
  const end = new Date(show.ends_at);
  const now = Date.now();
  const isLive = now >= start.getTime() && now < end.getTime();
  const isPast = now >= end.getTime();
  const isPremium = show.is_premium_event;
  const startedInMin = Math.round((start.getTime() - now) / 60000);
  const startsInLabel = !isLive && !isPast
    ? (startedInMin < 60 ? `in ${startedInMin} min` : startedInMin < 1440 ? `in ${Math.round(startedInMin / 60)}h` : null)
    : null;

  return (
    <div
      data-testid={`showcard-${show.schedule_id}`}
      className={`relative flex gap-3 rounded-lg overflow-hidden border transition-all ${
        isLive ? "border-[#E50914]/60 bg-[#E50914]/[0.06]" : "border-white/5 bg-[#0f0f0f] hover:border-white/15"
      }`}
    >
      {/* Poster */}
      <div className="relative w-28 sm:w-32 shrink-0 bg-black">
        {show.poster ? (
          <img src={show.poster} alt={show.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-gray-700"><Film className="w-8 h-8" /></div>
        )}
        {isPremium && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold bg-amber-500/95 text-black rounded px-1.5 py-0.5">
            <Sparkles className="w-2.5 h-2.5" /> Premium
          </span>
        )}
      </div>
      {/* Body */}
      <div className="flex-1 min-w-0 py-3 pr-3 flex flex-col">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded px-1.5 py-0.5"
            style={{ background: `${show.channel_color}25`, color: show.channel_color, border: `1px solid ${show.channel_color}55` }}
          >
            <Radio className="w-2.5 h-2.5" /> {show.channel_name}
          </span>
          {isLive && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-[#E50914] text-white rounded px-1.5 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> On Now
            </span>
          )}
          {isPast && (
            <span className="text-[10px] uppercase tracking-widest font-bold text-gray-600">Ended</span>
          )}
        </div>
        <h4 className="font-bold text-base leading-tight truncate">{show.title}</h4>
        <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-white">{fmtTime(show.starts_at)}</span>
          <span>→</span>
          <span>{fmtTime(show.ends_at)}</span>
          <span className="text-gray-600">·</span>
          <span>{show.duration_min} min</span>
          {startsInLabel && <span className="text-amber-400">· starts {startsInLabel}</span>}
        </div>
        {(show.genres?.length || 0) > 0 && (
          <p className="text-[11px] text-gray-500 mt-1 truncate">{show.genres.slice(0, 3).join(" · ")}</p>
        )}
        <div className="mt-auto pt-3 flex items-center gap-2 flex-wrap">
          {isPremium ? (
            <>
              <button
                data-testid={`buy-ticket-${show.schedule_id}`}
                onClick={onBuy}
                disabled={buying || isPast}
                className="bg-gradient-to-br from-amber-500 to-[#E50914] hover:brightness-110 text-white px-3.5 py-1.5 rounded text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
              >
                {buying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ticket className="w-3.5 h-3.5" />}
                ${show.ticket_price.toFixed(2)} {show.ticket_currency.toUpperCase()}
              </button>
              <button
                data-testid={`group-buy-${show.schedule_id}`}
                onClick={onBuyGroup}
                disabled={isPast}
                className="border border-amber-500/50 hover:bg-amber-500/10 text-amber-300 px-3 py-1.5 rounded text-xs font-bold flex items-center gap-1.5 disabled:opacity-40"
                title="Buy 2-8 tickets together — auto-creates a Watch Party room"
              >
                <Users className="w-3.5 h-3.5" /> Group
              </button>
            </>
          ) : (
            <button
              data-testid={`tune-in-${show.schedule_id}`}
              onClick={() => nav(`/live/${show.channel_slug}`)}
              disabled={isPast}
              className="bg-[#E50914] hover:bg-[#b3070f] text-white px-3.5 py-1.5 rounded text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
            >
              <Radio className="w-3.5 h-3.5" />
              {isLive ? "Tune in" : isPast ? "Ended" : "Watch live"}
            </button>
          )}
          {!isPast && (
            <button
              data-testid={`reminder-toggle-${show.schedule_id}`}
              onClick={onToggleReminder}
              title={hasReminder ? "Remove reminder" : "Remind me before this show starts"}
              className={`px-2 py-1.5 rounded text-xs flex items-center gap-1 border transition-colors ${
                hasReminder ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-white/10 text-gray-400 hover:border-white/30 hover:text-white"
              }`}
            >
              {hasReminder ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
              {hasReminder ? "Reminding" : "Remind me"}
            </button>
          )}
          {isPremium && show.on_demand_unlock && (
            <span className="text-[10px] text-amber-400/80 uppercase tracking-widest">+ 24h replay</span>
          )}
        </div>
      </div>
    </div>
  );
}

function GroupBookingModal({ show, onClose }) {
  const [quantity, setQuantity] = useState(4);
  const [emails, setEmails] = useState([""]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const total = (show.ticket_price * quantity).toFixed(2);
  const currency = show.ticket_currency.toUpperCase();
  const guestSeats = quantity - 1;

  const setQty = (n) => {
    const q = Math.max(2, Math.min(8, n));
    setQuantity(q);
    // Trim or pad emails to match guest seats
    const guests = q - 1;
    setEmails((prev) => {
      const next = [...prev];
      while (next.length < guests) next.push("");
      while (next.length > guests) next.pop();
      return next;
    });
  };

  useEffect(() => {
    // initial pad emails to guestSeats
    if (emails.length !== guestSeats) setEmails(Array(guestSeats).fill(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = emails.map((s) => s.trim().toLowerCase()).filter(Boolean);
    // Light client-side validation
    for (const em of cleaned) {
      if (!/^\S+@\S+\.\S+$/.test(em)) {
        toast.error(`"${em}" is not a valid email`);
        return;
      }
    }
    const dupes = cleaned.length !== new Set(cleaned).size;
    if (dupes) { toast.error("Duplicate emails — please remove repeats"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/gcr/tickets/group-booking", {
        schedule_id: show.schedule_id,
        origin_url: window.location.origin,
        quantity,
        recipient_emails: cleaned,
        gift_message: message.trim() || undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start group checkout");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="group-booking-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
      onClick={() => !busy && onClose()}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-gradient-to-br from-[#1a1208] to-[#0a0a0a] border border-amber-500/40 rounded-xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <button type="button" onClick={() => !busy && onClose()} disabled={busy} className="absolute top-3 right-3 text-gray-400 hover:text-white" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-6 h-6 text-amber-400" />
          <h3 className="text-xl font-black">Group booking</h3>
        </div>
        <p className="text-sm text-gray-400 mb-5">
          Buy 2-8 tickets together for <span className="text-white font-semibold">{show.title}</span>. We'll auto-create a private Watch Party room so you can all watch together.
        </p>

        <div className="bg-black/40 rounded-lg p-4 mb-5">
          <label className="block text-xs uppercase tracking-widest text-gray-400 mb-2">How many seats?</label>
          <div className="flex items-center justify-between gap-3">
            <button type="button" data-testid="group-qty-minus" onClick={() => setQty(quantity - 1)} disabled={quantity <= 2} className="bg-white/10 hover:bg-white/15 disabled:opacity-30 p-2 rounded">
              <Minus className="w-4 h-4" />
            </button>
            <div className="flex-1 text-center">
              <div className="text-5xl font-black text-amber-400">{quantity}</div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">seats · 2-8 max</div>
            </div>
            <button type="button" data-testid="group-qty-plus" onClick={() => setQty(quantity + 1)} disabled={quantity >= 8} className="bg-white/10 hover:bg-white/15 disabled:opacity-30 p-2 rounded">
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10 text-sm">
            <div>
              <span className="text-gray-400">{quantity} × ${show.ticket_price.toFixed(2)} {currency}</span>
            </div>
            <div className="text-2xl font-black text-white">${total} <span className="text-sm text-gray-500 font-semibold">{currency}</span></div>
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-xs uppercase tracking-widest text-gray-400 mb-2">
            <Mail className="inline w-3 h-3 mr-1" /> Friend emails ({guestSeats} guest seat{guestSeats === 1 ? "" : "s"}) <span className="text-gray-600">— optional</span>
          </label>
          <div className="space-y-2">
            {emails.map((em, i) => (
              <input
                key={i}
                data-testid={`group-email-${i}`}
                type="email"
                value={em}
                onChange={(e) => setEmails((prev) => prev.map((v, idx) => idx === i ? e.target.value : v))}
                placeholder={`Guest ${i + 1} email (skip to keep slot open)`}
                className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm"
                disabled={busy}
              />
            ))}
          </div>
          <p className="text-[10px] text-gray-500 mt-2">
            Emails get an instant ticket + auto-claim if they sign up. Leave blank to share the Watch Party link manually after checkout.
          </p>
        </div>

        <div className="mb-5">
          <label className="block text-xs uppercase tracking-widest text-gray-400 mb-1">Note to friends (optional)</label>
          <textarea
            data-testid="group-message"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 280))}
            rows={2}
            placeholder="Movie night at 9 PM — pizza on me!"
            className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm resize-none"
            disabled={busy}
          />
          <p className="text-[10px] text-gray-500 mt-1 text-right">{message.length}/280</p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-white/10">
          <button type="button" onClick={onClose} disabled={busy} className="btn-glass px-5 py-2.5 rounded text-sm">Cancel</button>
          <button
            data-testid="group-buy-confirm"
            type="submit"
            disabled={busy}
            className="bg-gradient-to-br from-amber-500 to-[#E50914] hover:brightness-110 text-white px-6 py-2.5 rounded text-sm font-bold flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening Stripe…</> : <><Ticket className="w-4 h-4" /> Pay ${total} {currency}</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function GroupSuccessModal({ group, onClose }) {
  const nav = useNavigate();
  const wpLink = group.watch_party_room_id ? `${window.location.origin}/watchparty/${group.watch_party_room_id}` : null;
  const copy = async () => {
    if (!wpLink) return;
    try {
      await navigator.clipboard.writeText(wpLink);
      toast.success("Invite link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please copy the URL manually");
    }
  };
  return (
    <div
      data-testid="group-success-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-lg bg-gradient-to-br from-[#1a1208] to-[#0a0a0a] border border-amber-500/50 rounded-xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-white" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/40 mb-3">
            <Users className="w-7 h-7 text-amber-400" />
          </div>
          <h3 className="text-2xl font-black mb-1">Group booking confirmed!</h3>
          <p className="text-sm text-gray-400">
            {group.quantity} tickets to <span className="text-white font-semibold">{group.video?.title}</span> · ${group.total_paid?.toFixed(2)} {group.currency?.toUpperCase()}
          </p>
        </div>

        {wpLink && (
          <div className="bg-black/40 rounded-lg p-4 mb-5 border border-amber-500/20">
            <div className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Watch Party link
            </div>
            <div className="flex items-center gap-2">
              <input readOnly value={wpLink} data-testid="group-watchparty-link" className="flex-1 bg-black/60 border border-white/15 rounded px-3 py-2 text-xs font-mono truncate" />
              <button data-testid="group-copy-link" onClick={copy} className="btn-glass px-3 py-2 rounded text-xs whitespace-nowrap flex items-center gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-2">Share this with the friends you invited. They'll auto-join the synced playback when the show starts.</p>
          </div>
        )}

        <div className="space-y-2 mb-5">
          <div className="text-xs uppercase tracking-widest text-gray-400 font-bold">Your seats</div>
          {group.seats?.map((seat) => (
            <div key={seat.ticket_id} className="flex items-center gap-3 bg-[#0f0f0f] border border-white/5 rounded px-3 py-2">
              <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-black ${seat.is_host ? "bg-amber-500/20 text-amber-300" : seat.claimed ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-gray-500"}`}>
                #{seat.seat_number}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">
                  {seat.is_host ? "You (host)" : seat.name || seat.email || <span className="text-gray-500 italic">Unclaimed slot</span>}
                </div>
                {seat.email && !seat.is_host && (
                  <div className="text-[11px] text-gray-500 truncate">{seat.email}</div>
                )}
                {seat.access_code && (
                  <div
                    data-testid={`seat-code-${seat.ticket_id}`}
                    onClick={() => {
                      navigator.clipboard?.writeText(seat.access_code).then(
                        () => toast.success("Access code copied"),
                        () => toast.error("Couldn't copy")
                      );
                    }}
                    role="button"
                    className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono text-amber-400 hover:text-amber-300 cursor-pointer"
                    title="Tap to copy — share with the seat holder"
                  >
                    <Ticket className="w-2.5 h-2.5" /> {seat.access_code} <Copy className="w-2.5 h-2.5 opacity-60" />
                  </div>
                )}
              </div>
              {seat.claimed ? (
                <span className="text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded px-1.5 py-0.5">Confirmed</span>
              ) : (
                <span className="text-[10px] uppercase tracking-widest bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded px-1.5 py-0.5">Pending</span>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 pt-4 border-t border-white/10">
          <button onClick={onClose} className="btn-glass px-4 py-2 rounded text-sm">Done</button>
          {wpLink && (
            <button
              data-testid="group-open-watchparty"
              onClick={() => nav(`/watchparty/${group.watch_party_room_id}`)}
              className="bg-gradient-to-br from-amber-500 to-[#E50914] hover:brightness-110 text-white px-5 py-2 rounded text-sm font-bold flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" /> Open Watch Party
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

